import { getConsentState, onConsentChange } from "./consent";

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let scriptLoaded = false;
let pendingPageview: string | null = null;

function consentAllowsAnalytics(): boolean {
  return getConsentState().analytics === true;
}

function loadGtag(): boolean {
  if (!MEASUREMENT_ID) return false;
  if (typeof window === "undefined") return false;
  if (!consentAllowsAnalytics()) return false;
  if (scriptLoaded) return true;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };
    window.gtag("js", new Date());
  }
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });

  if (!document.querySelector(`script[data-ga-id="${MEASUREMENT_ID}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.dataset.gaId = MEASUREMENT_ID;
    document.head.appendChild(script);
  }
  scriptLoaded = true;
  return true;
}

if (typeof window !== "undefined") {
  onConsentChange(() => {
    if (consentAllowsAnalytics() && pendingPageview) {
      const path = pendingPageview;
      pendingPageview = null;
      trackPageview(path);
    }
  });
}

export function trackPageview(path: string) {
  if (!MEASUREMENT_ID) return;
  if (!consentAllowsAnalytics()) {
    pendingPageview = path;
    return;
  }
  if (!loadGtag() || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    send_to: MEASUREMENT_ID,
  });
}

export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (!MEASUREMENT_ID) return;
  if (!consentAllowsAnalytics()) return;
  if (!loadGtag() || !window.gtag) return;
  window.gtag("event", name, params);
}

export const isAnalyticsEnabled = () => Boolean(MEASUREMENT_ID) && consentAllowsAnalytics();
